import { PINCH_ZOOM_SENSITIVITY } from "./gantt-constants";
import { clampPxPerDay } from "./layout-gantt";

export const MIN_ZOOM_DELTA = 0.0001;

const TOUCH_POINT_COUNT_FOR_PINCH = 2;
const FIRST_PINCH_TOUCH_INDEX = 0;
const SECOND_PINCH_TOUCH_INDEX = 1;
const TOUCH_MIDPOINT_DIVISOR = 2;

export interface PinchState {
  initialDistance: number;
  initialPxPerDay: number;
}

export interface TouchPair {
  firstTouch: Touch;
  secondTouch: Touch;
}

export interface ApplyZoomAtAnchorParams {
  anchorViewportX: number;
  scaleFactor: number;
}

interface SelectTouchMoveZoomParamsParams {
  touches: TouchList;
  pinchState: PinchState | null;
  timelineNode: HTMLDivElement;
  currentPxPerDay: number;
}

export function selectWheelScaleFactor(event: WheelEvent): number {
  return Math.exp(-event.deltaY * PINCH_ZOOM_SENSITIVITY);
}

export function selectAnchoredScrollLeft({
  anchorContentX,
  anchorViewportX,
  ratio,
}: {
  anchorContentX: number;
  anchorViewportX: number;
  ratio: number;
}): number {
  return Math.max(0, anchorContentX * ratio - anchorViewportX);
}

export function selectTouchPair(touches: TouchList): TouchPair | null {
  if (touches.length !== TOUCH_POINT_COUNT_FOR_PINCH) {
    return null;
  }

  const firstTouch = touches.item(FIRST_PINCH_TOUCH_INDEX);
  const secondTouch = touches.item(SECOND_PINCH_TOUCH_INDEX);
  if (!firstTouch || !secondTouch) {
    return null;
  }

  return {
    firstTouch,
    secondTouch,
  };
}

export function selectTouchDistance({
  firstTouch,
  secondTouch,
}: TouchPair): number {
  const deltaX = firstTouch.clientX - secondTouch.clientX;
  const deltaY = firstTouch.clientY - secondTouch.clientY;
  return Math.hypot(deltaX, deltaY);
}

function selectTouchMidpointViewportX({
  touchPair,
  timelineNode,
}: {
  touchPair: TouchPair;
  timelineNode: HTMLDivElement;
}): number {
  const rect = timelineNode.getBoundingClientRect();
  return (
    (touchPair.firstTouch.clientX + touchPair.secondTouch.clientX) /
      TOUCH_MIDPOINT_DIVISOR -
    rect.left
  );
}

function selectTouchMoveScaleFactor({
  targetPxPerDay,
  previousPxPerDay,
}: {
  targetPxPerDay: number;
  previousPxPerDay: number;
}): number {
  return previousPxPerDay > 0 ? targetPxPerDay / previousPxPerDay : 1;
}

export function selectTouchMoveZoomParams({
  touches,
  pinchState,
  timelineNode,
  currentPxPerDay,
}: SelectTouchMoveZoomParamsParams): ApplyZoomAtAnchorParams | null {
  const touchPair = selectTouchPair(touches);
  if (!pinchState || !touchPair || pinchState.initialDistance <= 0) {
    return null;
  }

  const distance = selectTouchDistance(touchPair);
  const targetPxPerDay = clampPxPerDay(
    pinchState.initialPxPerDay * (distance / pinchState.initialDistance),
  );

  return {
    anchorViewportX: selectTouchMidpointViewportX({
      touchPair,
      timelineNode,
    }),
    scaleFactor: selectTouchMoveScaleFactor({
      targetPxPerDay,
      previousPxPerDay: currentPxPerDay,
    }),
  };
}
